import styled from '@emotion/styled';
import { css, Global } from '@emotion/core';
import { Link as RouterLink } from 'react-router-dom';
import ProximaNovaLight from './fonts/ProximaNova-Light.woff';
import ProximaNovaLight2 from './fonts/ProximaNova-Light.woff2';
import ProximaNova from './fonts/ProximaNova-Regular.woff';
import ProximaNova2 from './fonts/ProximaNova-Regular.woff2';
import ProximaNovaSemiBold from './fonts/ProximaNova-Semibold.woff';
import ProximaNovaSemiBold2 from './fonts/ProximaNova-Semibold.woff2';
import ProximaNovaBold from './fonts/ProximaNova-Bold.woff';
import ProximaNovaBold2 from './fonts/ProximaNova-Bold.woff2';
import KizenTypography from './kizentypo';
// This is a base styling used and shared by multiple components.
// Most of the time you'll be using the KizenTypography export
// unless you need some lower level customization

export const baseFontSize = 10; // px

// This is the proportion of the baseline compared to the font size
export const baselineConstant = 3 / 14;

export const fontWeights = {
  light: 100,
  regular: 400,
  bold: 600,
  black: 900,
};

export const monoFontFamily =
  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', monospace";

export const fontSizes = {
  hero: '3.2em',
  header: '2.4em',
  subheader: '1.8em',
  text: '1.4em',
  button: '1.3em',
  buttonLabel: '1.2em',
  small: '1.1em',
  micro: '1em',
  tiny: '0.8rem',
};

export const bottomMarginOptions = {
  xl: '3rem',
  lg: '2.5rem',
  md: '2rem',
  sm: '1.5rem',
  xs: '1em',
  none: 0,
};

export const regularFontCss = `
  @font-face {
    font-family: 'Proxima Nova';
    src: url(${ProximaNova}) format('woff'),
      url(${ProximaNova2}) format('woff2');
    font-weight: ${fontWeights.regular};
    font-style: normal;
  }
`;

export const lightFontCss = `
  @font-face {
    font-family: 'Proxima Nova';
    src: url(${ProximaNovaLight}) format('woff'),
      url(${ProximaNovaLight2}) format('woff2');
    font-weight: ${fontWeights.light};
    font-style: normal;
  }
`;

export const truncateCss = css`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;

export const Typing = () => {
  return (
    <Global
      styles={css`
        * {
          font-family: 'Proxima Nova', sans-serif;
          font-size: ${baseFontSize}px;
          line-height: 1;
        }

        @font-face {
          font-family: 'Proxima Nova';
          src:
            url(${ProximaNovaLight}) format('woff'),
            url(${ProximaNovaLight2}) format('woff2');
          font-weight: ${fontWeights.light};
          font-style: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          src:
            url(${ProximaNova}) format('woff'),
            url(${ProximaNova2}) format('woff2');
          font-weight: ${fontWeights.regular};
          font-style: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          src:
            url(${ProximaNovaSemiBold}) format('woff'),
            url(${ProximaNovaSemiBold2}) format('woff2');
          font-weight: ${fontWeights.bold};
          font-style: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          src:
            url(${ProximaNovaBold}) format('woff'),
            url(${ProximaNovaBold2}) format('woff2');
          font-weight: ${fontWeights.black};
          font-style: normal;
        }

        :root {
          --font-family-sans-serif: 'Proxima Nova', sans-serif;
          -webkit-font-smoothing: antialiased;
        }
      `}
    />
  );
};

export const textCss = (
  size = 'text',
  weight = 'regular',
  margin = 'none',
  textTransform = 'none',
  color = 'var(--color-font-primary)',
  fontStyle = 'normal',
  textDecoration
) => css`
  font-size: ${fontSizes[size] || size};
  font-weight: ${fontWeights[weight] || weight};
  color: ${color};
  margin-bottom: ${bottomMarginOptions[margin]};
  font-style: ${fontStyle};
  ${textTransform ? `text-transform: ${textTransform};` : ''}
  ${textDecoration ? `text-decoration: ${textDecoration};` : ''}
`;

const Text = styled.p(
  ({ size, weight, margin, textDecoration, textTransform, color, fontStyle }) =>
    textCss(
      size,
      weight,
      margin,
      textTransform,
      color,
      fontStyle,
      textDecoration
    )
);
const TextSpan = styled.span(
  ({ size, weight, margin, textDecoration, textTransform, color }) =>
    textCss(size, weight, margin, textTransform, color, textDecoration)
);

export const labelCss = (
  size = 'text',
  weight = 'regular',
  margin = 'none',
  textTransform = null,
  color = null
) => css`
  color: unset;
  text-transform: uppercase;
  ${textCss(size, weight, margin, textTransform, color)};
`;
const Label = styled.p(({ size, weight, margin, textTransform, color }) =>
  labelCss(size, weight, margin, textTransform, color)
);

export const linkCss = (
  size = 'text',
  weight = 'regular',
  margin = 'none',
  textTransform = 'none'
) => css`
  ${textCss(size, weight, margin, textTransform)};
  color: var(--color-button-secondary-default);
  :hover {
    color: var(--color-button-secondary-hover);
  }
  :active {
    color: var(--color-button-secondary-hover);
  }
`;

export const StyledSpanLink = styled.span(
  ({ size, weight, margin, textTransform }) => css`
    ${linkCss(size, weight, margin, textTransform)}
    color: var(--color-button-secondary-default);
    &:hover {
      text-decoration: underline;
      cursor: pointer;
      color: var(--color-button-secondary-default);
    }
    &:active {
      color: var(--color-button-secondary-hover);
    }
  `
);

// Link component throws an exception if `to` is undefined
// So we create a wrapper that falls back to a span if no `to` is provided
const RouterLinkWithFallback = (props) => {
  if (props.to) {
    return <RouterLink {...props} />;
  }
  return <StyledSpanLink {...props} />;
};

const Link = styled(RouterLinkWithFallback, {
  shouldForwardProp: (prop) => prop !== 'textTransform',
})(
  ({ size, weight, margin, textTransform }) => css`
    ${linkCss(size, weight, margin, textTransform)}
  `
);

const UtilityLink = styled('a', {
  shouldForwardProp: (prop) => prop !== 'textTransform',
})(
  ({ size, weight, margin, textTransform, onClick }) => css`
    ${linkCss(size, weight, margin, textTransform)}
    ${onClick &&
    css`
      :hover {
        cursor: pointer;
      }
    `}
  `
);

export const notificationCss = (
  size = 'micro',
  weight = 'regular',
  margin = 'none'
) => css`
  ${textCss(size, weight, margin)};
  background-color: var(--color-button-secondary-default);
  color: var(--color-background-white);
  padding: 0.3rem 0.65rem;
  vertical-align: top;
  border-radius: 1rem;
  text-align: center;
`;
const Notification = styled.span(({ size, weight, margin }) =>
  notificationCss(size, weight, margin)
);

const typographyDefaultProps = {
  size: 'text',
  weight: 'regular',
  margin: 'none',
};
Text.defaultProps = typographyDefaultProps;
TextSpan.defaultProps = typographyDefaultProps;
Label.defaultProps = typographyDefaultProps;
Link.defaultProps = typographyDefaultProps;
UtilityLink.defaultProps = typographyDefaultProps;

export {
  Text,
  TextSpan,
  Label,
  Link,
  UtilityLink,
  Notification,
  KizenTypography,
};
